import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import { Button } from "@progress/kendo-react-buttons";
import { AutoComplete } from "@progress/kendo-react-dropdowns";
import { Label } from "@progress/kendo-react-labels";
import { CommandCell } from '../../forms/CommandCell';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { filterBy } from "@progress/kendo-data-query";

import './Venue.scss';

export const AdministratorVenuePOSNotes = (props) => {
    const dispatch = useDispatch();

    const editField = "inEdit";

    const [venuePOSNotes, setVenuePOSNotes] = useState([]);
    const [areVenuePOSNotesLoading, setAreVenuePOSNotesLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");
    const [notes, setNotes] = useState([]);
    const [noteDescriptions, setNoteDescriptions] = useState([]);

    const [autocompleteState, setAutocompleteState] = useState({
        data: [],
        value: "",
    });

    const getVenuePOSNotes = () => {
        setAreVenuePOSNotesLoading(true);

        const dataItem = {
            venueKey: props.venueKey,
        };
    
        fetch(`/api/administrator/venueposnotes`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setAreVenuePOSNotesLoading(false);
                setVenuePOSNotes(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenuePOSNotesLoading(false);
                setLoadingError(error.message);
            });
    }

    const deleteVenuePOSNote = (venuePOSNoteKey) => {
        const dataItem = {
            venuePOSNoteKey: venuePOSNoteKey
        }

        fetch("/api/administrator/venueposnote",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted item successfully");

                getVenuePOSNotes();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const saveVenuePOSNote = () => {
        const note = notes.find(element => element.description === autocompleteState.value);

        if (!note) {
            addNotificationWithTimeout(dispatch, "error", "You must select a note");
            return;
        }

        const dataItem = {
            venueKey: props.venueKey,
            posNoteId: note.id,
        }

        fetch("/api/administrator/venueposnote",
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Created item successfully");

                getVenuePOSNotes();

                let newAutocompleteState = autocompleteState;
                newAutocompleteState.value = "";
                setAutocompleteState(newAutocompleteState);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const discard = (dataItem) => {
        const newVenuePOSNotes = [...venuePOSNotes];
        newVenuePOSNotes.splice(0, 1);
        setVenuePOSNotes(newVenuePOSNotes);
    };

    const remove = (dataItem) => {
        deleteVenuePOSNote(dataItem.venuePOSNoteKey);
    };

    const MyCommandCell = (props) => (
        <CommandCell
            {...props}
            canEdit={false}
            remove={remove}
            discard={discard}
            editField={editField}
            keyField="venuePOSNoteKey"
        />
    );

    const itemChange = (event) => {
        const field = event.field || "";
        const newVenuePOSNotes = venuePOSNotes.map((item) =>
            item.venuePOSNoteKey === event.dataItem.venuePOSNoteKey
                ? { ...item, [field]: event.value }
                : item
        );
        setVenuePOSNotes(newVenuePOSNotes);
    };

    const getPOSNotes = () => {
        fetch(`/api/administrator/pos/additionalnotes`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                setNotes(result);

                let localNoteDescriptions = [];
                result.forEach(element => localNoteDescriptions.push(element.description));

                setNoteDescriptions(localNoteDescriptions);
                setAutocompleteState({
                    data: localNoteDescriptions,
                    value: "",
                });
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    };

    const filterNotes = (value) => {
        const data = noteDescriptions;

        const filter = {
            value: value,
            operator: "contains",
            ignoreCase: true,
        };       

        return value ? filterBy(data, filter) : data;
    };

    const noteChanged = (event) => {
        const value = event.value;

        setAutocompleteState({
            data: filterNotes(value),
            value: value,
        });
    };

    useEffect(() => {
        if (props.venueKey) {
            getPOSNotes();
            getVenuePOSNotes();
        }
    }, []);

    return (
        <div>
            <div className="mb-3">
                <Label>Add note:</Label> 

                <AutoComplete
                    id={"note"}
                    name={"note"}
                    data={autocompleteState.data}   
                    value={autocompleteState.value}
                    placeholder="e.g. 2 drink minimum"
                    onChange={noteChanged}
                />

                <Button
                    themeColor={"primary"}
                    type={"button"}
                    onClick={saveVenuePOSNote}
                    className="mt-2"
                    >
                    Save
                </Button>
            </div>
            {areVenuePOSNotesLoading ? <LoadingPanel /> : (loadingError === "" ? (
                <Grid
                    data={venuePOSNotes}
                    onItemChange={itemChange}
                    editField={editField}
                    dataItemKey={"venuePOSNoteKey"}
                    resizable={true}
                    scrollable="none"
                >
                    <Column field="description" title={"Note"} />
                    <Column cell={MyCommandCell} width="150px" />
                </Grid>
            ) : <></>)
            }
        </div>
    );
}